// Phase 6.3.1 — Skeleton primitive.
//
// Loading placeholder with a shimmer. Honors prefers-reduced-motion
// two ways: the CSS shimmer (globals.css) is gated by a media query,
// AND the component reads matchMedia so it can drop the animated
// class entirely (static placeholder) for reduced-motion users —
// which also makes the behaviour unit-testable. 6.3.2 migrates the
// ad-hoc loading skeletons onto this.

import { useEffect, useState, type CSSProperties } from "react";

export type SkeletonVariant = "text" | "rectangle" | "circle";

export interface SkeletonProps {
  variant?: SkeletonVariant;
  /** CSS width (e.g. "120px", "100%"). */
  width?: string | number;
  /** CSS height (e.g. "1rem"). */
  height?: string | number;
  /** For variant="text": number of lines to render (default 1). The
   *  last line is rendered shorter to mimic a paragraph. */
  lines?: number;
  className?: string;
}

function usePrefersReducedMotion(): boolean {
  const read = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;
  const [reduced, setReduced] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

function dim(v: string | number | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

export function Skeleton({
  variant = "rectangle",
  width,
  height,
  lines = 1,
  className = "",
}: SkeletonProps) {
  const reduced = usePrefersReducedMotion();
  // Static base always; the animated shimmer overlay only when motion
  // is allowed.
  const base = reduced ? "bg-theme-bg-chip-subtle" : "skeleton-shimmer";

  if (variant === "text") {
    const count = Math.max(1, lines);
    return (
      <div
        data-testid="skeleton"
        data-variant="text"
        data-animated={!reduced}
        className={`flex flex-col gap-snug ${className}`}
        aria-hidden
      >
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            data-testid="skeleton-line"
            className={`${base} h-3 rounded-sm`}
            style={{
              width:
                i === count - 1 && count > 1 ? "60%" : (dim(width) ?? "100%"),
            }}
          />
        ))}
      </div>
    );
  }

  const style: CSSProperties = {
    width: dim(width) ?? (variant === "circle" ? "2.5rem" : "100%"),
    height: dim(height) ?? (variant === "circle" ? "2.5rem" : "1rem"),
  };

  return (
    <div
      data-testid="skeleton"
      data-variant={variant}
      data-animated={!reduced}
      aria-hidden
      style={style}
      className={[
        base,
        variant === "circle" ? "rounded-full" : "rounded-sm",
        className,
      ].join(" ")}
    />
  );
}
